// Integration layer between observable analytics and partner's enhanced analytics
// Combines observable predictions with existing analytics infrastructure
// Enhanced with error handling, validation, and performance optimization

import type { OrderBookEntry } from '../types';
import type { AnalyticsData } from '../types';
import { BookStateTracker, TradePatternAnalyzer } from './observableAnalytics';
import { calculateObservableFillProbability, calculateObservableMarketImpact, createMarketActivityProfile } from './observablePredictions';
import { adaptiveParams, type AdaptiveParameters } from './adaptiveParameters';
import { analyticsCacheManager } from './analyticsCache';

export interface AnalyticsError {
  code: string;
  message: string;
  component: string;
  timestamp: number;
  severity: 'low' | 'medium' | 'high';
  context?: any;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface EnhancedObservableAnalytics extends AnalyticsData {
  observable: {
    fillProbability: ReturnType<typeof calculateObservableFillProbability> | null;
    marketImpact: ReturnType<typeof calculateObservableMarketImpact> | null;
    activityProfile: ReturnType<typeof createMarketActivityProfile> | null;
    confidence: {
      overall: 'low' | 'medium' | 'high';
      dataPoints: number;
      methodology: string[];
    };
  };
  predictions: {
    nextMoveDirection: 'up' | 'down' | 'sideways';
    confidence: number;
    timeHorizon: string;
    reasoning: string[];
  };
  errors: AnalyticsError[];
  validation: ValidationResult;
  performance: {
    computeTime: number;
    cacheHitRate: number;
    dataQuality: number;
  };
}

export class ObservableAnalyticsIntegrator {
  private bookTracker: BookStateTracker;
  private tradeAnalyzer: TradePatternAnalyzer;
  private errors: AnalyticsError[] = [];
  private readonly MAX_ERRORS = 100;

  constructor() {
    // BookStateTracker / TradePatternAnalyzer currently use default internal configs.
    this.bookTracker = new BookStateTracker();
    this.tradeAnalyzer = new TradePatternAnalyzer();
  }

  // Data validation methods
  private validateOrderBook(orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] }): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!orderBook) {
      errors.push('Order book is null or undefined');
      return { isValid: false, errors, warnings };
    }

    if (!Array.isArray(orderBook.bids) || !Array.isArray(orderBook.asks)) {
      errors.push('Order book bids/asks must be arrays');
      return { isValid: false, errors, warnings };
    }

    // Check for valid price and size data
    const invalidBids = orderBook.bids.filter(bid =>
      typeof bid.price !== 'number' || typeof bid.size !== 'number' ||
      bid.price <= 0 || bid.size <= 0 || !isFinite(bid.price) || !isFinite(bid.size)
    );

    const invalidAsks = orderBook.asks.filter(ask =>
      typeof ask.price !== 'number' || typeof ask.size !== 'number' ||
      ask.price <= 0 || ask.size <= 0 || !isFinite(ask.price) || !isFinite(ask.size)
    );

    if (invalidBids.length > 0) {
      errors.push(`${invalidBids.length} invalid bid entries`);
    }

    if (invalidAsks.length > 0) {
      errors.push(`${invalidAsks.length} invalid ask entries`);
    }

    // Check for reasonable spread
    if (orderBook.bids.length > 0 && orderBook.asks.length > 0) {
      const bestBid = orderBook.bids[0].price;
      const bestAsk = orderBook.asks[0].price;

      if (bestBid >= bestAsk) {
        errors.push('Crossed market: best bid >= best ask');
      }

      const spread = (bestAsk - bestBid) / bestBid;
      if (spread > 0.1) { // 10% spread seems excessive
        warnings.push(`Wide spread detected: ${(spread * 100).toFixed(2)}%`);
      }
    }

    // Check for empty book
    if (orderBook.bids.length === 0 && orderBook.asks.length === 0) {
      warnings.push('Empty order book');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  private validateTrades(trades: any[]): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!Array.isArray(trades)) {
      errors.push('Trades must be an array');
      return { isValid: false, errors, warnings };
    }

    const invalidTrades = trades.filter(trade =>
      !trade ||
      typeof trade.price !== 'number' || typeof trade.size !== 'number' ||
      trade.price <= 0 || trade.size <= 0 ||
      !isFinite(trade.price) || !isFinite(trade.size) ||
      !['buy', 'sell'].includes(trade.side)
    );

    if (invalidTrades.length > 0) {
      errors.push(`${invalidTrades.length} invalid trade entries`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  // Error handling methods
  private logError(error: Partial<AnalyticsError>): void {
    const fullError: AnalyticsError = {
      code: error.code || 'UNKNOWN_ERROR',
      message: error.message || 'Unknown error occurred',
      component: error.component || 'ObservableIntegrator',
      timestamp: Date.now(),
      severity: error.severity || 'medium',
      context: error.context
    };

    this.errors.push(fullError);

    // Keep only recent errors
    if (this.errors.length > this.MAX_ERRORS) {
      this.errors = this.errors.slice(-this.MAX_ERRORS);
    }

    // Log to console based on severity
    const logFn = fullError.severity === 'high' ? console.error :
                  fullError.severity === 'medium' ? console.warn : console.log;

    logFn(`[${fullError.component}] ${fullError.code}: ${fullError.message}`, fullError.context);
  }

  private safeExecute<T>(
    operation: () => T,
    fallback: T,
    context: { component: string; operation: string }
  ): T {
    try {
      return operation();
    } catch (error) {
      this.logError({
        code: 'COMPUTATION_FAILED',
        message: `${context.operation} failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        component: context.component,
        severity: 'medium',
        context: { error, operation: context.operation }
      });
      return fallback;
    }
  }


  // Update with new market data (with validation and error handling)
  updateMarketData(orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] }, trades: any[] = []): ValidationResult {
    const bookValidation = this.validateOrderBook(orderBook);
    const tradeValidation = this.validateTrades(trades);

    let hasErrors = false;

    if (bookValidation.isValid) {
      this.safeExecute(
        () => this.bookTracker.trackBookUpdate(orderBook, Date.now()),
        undefined,
        { component: 'BookTracker', operation: 'trackBookUpdate' }
      );
    } else {
      hasErrors = true;
      this.logError({
        code: 'INVALID_ORDERBOOK',
        message: 'Order book validation failed',
        component: 'DataValidation',
        severity: 'high',
        context: { errors: bookValidation.errors }
      });
    }

    if (tradeValidation.isValid) {
      for (const trade of trades) {
        this.safeExecute(
          () => this.tradeAnalyzer.addTrade(trade),
          undefined,
          { component: 'TradeAnalyzer', operation: 'addTrade' }
        );
      }
    } else {
      hasErrors = true;
      this.logError({
        code: 'INVALID_TRADES',
        message: 'Trade data validation failed',
        component: 'DataValidation',
        severity: 'medium',
        context: { errors: tradeValidation.errors }
      });
    }

    // Update adaptive parameters based on market conditions
    if (bookValidation.isValid) {
      this.safeExecute(
        () => {
          const priceHistory = this.extractPriceHistory(trades);
          adaptiveParams.updateParameters(orderBook, trades, priceHistory);
        },
        undefined,
        { component: 'AdaptiveParams', operation: 'updateParameters' }
      );
    }

    return {
      isValid: !hasErrors,
      errors: [...bookValidation.errors, ...tradeValidation.errors],
      warnings: [...bookValidation.warnings, ...tradeValidation.warnings]
    };
  }

  private extractPriceHistory(trades: any[]): Array<{ time: string; price: number }> {
    return trades
      .filter(trade => trade && typeof trade.price === 'number' && trade.time)
      .map(trade => ({ time: trade.time, price: trade.price }))
      .slice(-100); // Keep recent history
  }

  // Generate enhanced analytics combining partner's work with observable data
  generateEnhancedAnalytics(
    baseAnalytics: AnalyticsData,
    orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] },
    hypotheticalOrder?: { side: 'buy' | 'sell'; size: number; price: number }
  ): EnhancedObservableAnalytics {
    const startTime = performance.now();
    const validation = this.validateOrderBook(orderBook);
    const params = adaptiveParams.getCurrentParameters();

    // Initialize analytics result with error tracking
    let fillProbability = null;
    let marketImpact = null;
    let activityProfile = null;
    let bookAnalytics: any = null;
    let tradePatterns: any = null;

    // Safely get analytics data with caching
    bookAnalytics = this.safeExecute(
      () => analyticsCacheManager.getGeneralAnalytics(
        [orderBook],
        () => this.bookTracker.getRecentAnalytics(),
        params,
        'bookAnalytics'
      ),
      null,
      { component: 'BookTracker', operation: 'getRecentAnalytics' }
    );

    // Backwards-compatible accessor with error handling
    tradePatterns = this.safeExecute(
      () => {
        const analyzer = this.tradeAnalyzer as any;
        return analyzer.getPatterns?.() ?? analyzer.getObservations?.() ?? analyzer.analyzeTradeSequences?.(5) ?? null;
      },
      null,
      { component: 'TradeAnalyzer', operation: 'getPatterns' }
    );

    // Observable predictions (only if sufficient data and valid inputs)
    if (bookAnalytics && tradePatterns && hypotheticalOrder && validation.isValid) {
      // Validate hypothetical order
      if (this.validateHypotheticalOrder(hypotheticalOrder)) {
        fillProbability = this.safeExecute(
          () => analyticsCacheManager.getFillProbability(
            orderBook,
            hypotheticalOrder.side,
            hypotheticalOrder.size,
            hypotheticalOrder.price,
            [],
            () => calculateObservableFillProbability(
              orderBook,
              hypotheticalOrder.side,
              hypotheticalOrder.size,
              hypotheticalOrder.price,
              bookAnalytics,
              tradePatterns
            ),
            params
          ),
          null,
          { component: 'FillProbability', operation: 'calculate' }
        );

        marketImpact = this.safeExecute(
          () => analyticsCacheManager.getGeneralAnalytics(
            [orderBook, hypotheticalOrder.size, hypotheticalOrder.side],
            () => calculateObservableMarketImpact(
              orderBook,
              hypotheticalOrder.size,
              hypotheticalOrder.side,
              tradePatterns
            ),
            params,
            'marketImpact'
          ),
          null,
          { component: 'MarketImpact', operation: 'calculate' }
        );
      } else {
        this.logError({
          code: 'INVALID_HYPOTHETICAL_ORDER',
          message: 'Hypothetical order validation failed',
          component: 'DataValidation',
          severity: 'medium',
          context: { order: hypotheticalOrder }
        });
      }

      activityProfile = this.safeExecute(
        () => analyticsCacheManager.getGeneralAnalytics(
          [bookAnalytics, tradePatterns],
          () => createMarketActivityProfile(bookAnalytics, tradePatterns),
          params,
          'activityProfile'
        ),
        null,
        { component: 'ActivityProfile', operation: 'create' }
      );
    }

    // Calculate overall confidence
    const overallConfidence = this.calculateOverallConfidence(
      bookAnalytics,
      tradePatterns,
      fillProbability,
      marketImpact
    );

    // Generate market predictions with error handling
    const predictions = this.safeExecute(
      () => this.generateMarketPredictions(
        baseAnalytics,
        bookAnalytics,
        tradePatterns,
        orderBook
      ),
      {
        nextMoveDirection: 'sideways' as const,
        confidence: 0.3,
        timeHorizon: '5-15 minutes',
        reasoning: ['Insufficient data for prediction']
      },
      { component: 'Predictions', operation: 'generate' }
    );

    // Calculate performance metrics
    const computeTime = performance.now() - startTime;
    const cacheStats = analyticsCacheManager.getAllStats();
    const avgHitRate = Object.values(cacheStats).reduce((sum, stat) => sum + stat.hitRate, 0) / Object.keys(cacheStats).length;

    // Calculate data quality score
    const dataQuality = this.calculateDataQuality(validation, bookAnalytics, tradePatterns);

    return {
      ...baseAnalytics,
      observable: {
        fillProbability,
        marketImpact,
        activityProfile,
        confidence: overallConfidence
      },
      predictions,
      errors: this.getRecentErrors(10),
      validation,
      performance: {
        computeTime,
        cacheHitRate: avgHitRate,
        dataQuality
      }
    };
  }

  private validateHypotheticalOrder(order: { side: 'buy' | 'sell'; size: number; price: number }): boolean {
    return order &&
           ['buy', 'sell'].includes(order.side) &&
           typeof order.size === 'number' && order.size > 0 && isFinite(order.size) &&
           typeof order.price === 'number' && order.price > 0 && isFinite(order.price);
  }

  private calculateDataQuality(
    validation: ValidationResult,
    bookAnalytics: any,
    tradePatterns: any
  ): number {
    let score = 0.5; // Base score

    // Validation quality
    if (validation.isValid) {
      score += 0.2;
    } else {
      score -= validation.errors.length * 0.1;
    }

    // Data availability
    if (bookAnalytics && tradePatterns) {
      score += 0.2;
    }

    // Data freshness (simplified)
    if (bookAnalytics && bookAnalytics.priceLevelUpdates?.frequency > 1) {
      score += 0.1;
    }

    return Math.max(0, Math.min(1, score));
  }

  private getRecentErrors(count: number = 10): AnalyticsError[] {
    return this.errors.slice(-count);
  }

  private calculateOverallConfidence(
    bookAnalytics: any,
    tradePatterns: any,
    fillProbability: any,
    marketImpact: any
  ): { overall: 'low' | 'medium' | 'high'; dataPoints: number; methodology: string[] } {
    const methodology: string[] = ['Book state tracking'];
    let dataPoints = 0;
    let confidenceScores: number[] = [];

    if (bookAnalytics) {
      dataPoints += bookAnalytics.snapshots;
      methodology.push('Historical book analysis');
    }

    if (tradePatterns) {
      dataPoints += tradePatterns.priceImpactObservations.observations.length;
      methodology.push('Trade pattern analysis');
    }

    if (fillProbability) {
      methodology.push(fillProbability.methodology);
      confidenceScores.push(fillProbability.confidence === 'high' ? 0.8 :
                           fillProbability.confidence === 'medium' ? 0.6 : 0.4);
    }

    if (marketImpact) {
      methodology.push(marketImpact.methodology);
      confidenceScores.push(marketImpact.confidence === 'high' ? 0.8 :
                           marketImpact.confidence === 'medium' ? 0.6 : 0.4);
    }

    // Overall confidence based on data quality and consistency
    const avgConfidence = confidenceScores.length > 0 ?
      confidenceScores.reduce((sum, score) => sum + score, 0) / confidenceScores.length : 0.3;

    let overall: 'low' | 'medium' | 'high' = 'low';
    if (avgConfidence > 0.7 && dataPoints > 50) overall = 'high';
    else if (avgConfidence > 0.5 && dataPoints > 20) overall = 'medium';

    return {
      overall,
      dataPoints,
      methodology
    };
  }

  private generateMarketPredictions(
    baseAnalytics: AnalyticsData,
    bookAnalytics: any,
    tradePatterns: any,
    orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] }
  ): {
    nextMoveDirection: 'up' | 'down' | 'sideways';
    confidence: number;
    timeHorizon: string;
    reasoning: string[];
  } {
    const reasoning: string[] = [];
    let upSignals = 0;
    let downSignals = 0;
    let confidence = 0.3; // Base confidence

    // Signal 1: VWAP Analysis
    // `calculateAnalytics` returns `vwapAnalysis` (not `vwap`) — use optional chaining to be safe
    if ((baseAnalytics as any).vwapAnalysis?.distance !== undefined) {
      const dist = (baseAnalytics as any).vwapAnalysis.distance as number;
      if (Math.abs(dist) > 0.5) {
        if (dist > 0) {
          downSignals++;
          reasoning.push(`Price ${dist.toFixed(2)} above VWAP - potential reversion`);
        } else {
          upSignals++;
          reasoning.push(`Price ${Math.abs(dist).toFixed(2)} below VWAP - potential bounce`);
        }
      }
    }

    // Signal 2: Book Pressure
    const bp = (baseAnalytics as any).bookPressure;
    if (bp?.pressure !== undefined && Math.abs(bp.pressure) > 0.2) {
      if (bp.pressure > 0) {
        upSignals++;
        reasoning.push(`Strong buy pressure: ${(bp.pressure * 100).toFixed(1)}%`);
      } else {
        downSignals++;
        reasoning.push(`Strong sell pressure: ${Math.abs(bp.pressure * 100).toFixed(1)}%`);
      }
      confidence += 0.2;
    }


    // Signal 3: Depth Imbalance
    if (baseAnalytics.depthImbalance && Math.abs(baseAnalytics.depthImbalance.imbalancePercent) > 15) {
      if (baseAnalytics.depthImbalance.imbalance > 0) {
        upSignals++;
        reasoning.push(`Bid depth advantage: ${baseAnalytics.depthImbalance.imbalancePercent.toFixed(1)}%`);
      } else {
        downSignals++;
        reasoning.push(`Ask depth advantage: ${Math.abs(baseAnalytics.depthImbalance.imbalancePercent).toFixed(1)}%`);
      }
      confidence += 0.15;
    }

    // Signal 4: Recent Trade Patterns (if available)
    if (tradePatterns && tradePatterns.priceImpactObservations.observations.length > 5) {
      const recentTrades = tradePatterns.priceImpactObservations.observations.slice(-5);
      const avgPriceChange = recentTrades.reduce((sum: number, obs: any) => sum + (obs.priceChangePercent as number), 0) / recentTrades.length;


      if (Math.abs(avgPriceChange) > 0.1) {
        if (avgPriceChange > 0) {
          upSignals++;
          reasoning.push(`Recent upward price momentum: ${(avgPriceChange * 100).toFixed(2)}%`);
        } else {
          downSignals++;
          reasoning.push(`Recent downward price momentum: ${Math.abs(avgPriceChange * 100).toFixed(2)}%`);
        }
        confidence += 0.1;
      }
    }

    // Determine direction
    let nextMoveDirection: 'up' | 'down' | 'sideways' = 'sideways';

    if (upSignals > downSignals && upSignals > 1) {
      nextMoveDirection = 'up';
      confidence = Math.min(0.9, confidence + (upSignals - downSignals) * 0.1);
    } else if (downSignals > upSignals && downSignals > 1) {
      nextMoveDirection = 'down';
      confidence = Math.min(0.9, confidence + (downSignals - upSignals) * 0.1);
    } else {
      reasoning.push('Mixed signals suggest sideways movement');
    }

    // Time horizon based on market activity
    let timeHorizon = '5-15 minutes';
    if (bookAnalytics && bookAnalytics.priceLevelUpdates.frequency > 10) {
      timeHorizon = '1-5 minutes'; // High frequency market
    } else if (bookAnalytics && bookAnalytics.priceLevelUpdates.frequency < 2) {
      timeHorizon = '15-30 minutes'; // Low frequency market
    }

    return {
      nextMoveDirection,
      confidence: Math.round(confidence * 100) / 100,
      timeHorizon,
      reasoning
    };
  }

  // Get current data status with enhanced error information
  getDataStatus(): {
    bookSnapshots: number;
    tradeObservations: number;
    isReady: boolean;
    recommendedMinimumData: {
      bookSnapshots: number;
      tradeObservations: number;
    };
    errors: AnalyticsError[];
    cacheStats: any;
    adaptiveParams: AdaptiveParameters;
  } {
    const bookAnalytics = this.safeExecute(
      () => this.bookTracker.getRecentAnalytics(),
      null,
      { component: 'BookTracker', operation: 'getRecentAnalytics' }
    );

    const tradePatterns = this.safeExecute(
      () => {
        const analyzer = this.tradeAnalyzer as any;
        return analyzer.getPatterns?.() ?? analyzer.getObservations?.() ?? analyzer.analyzeTradeSequences?.(5) ?? null;
      },
      null,
      { component: 'TradeAnalyzer', operation: 'getPatterns' }
    );

    const bookSnapshots = this.safeExecute(
      () => {
        if (bookAnalytics && typeof (bookAnalytics as any).getDataPointCount === 'function') {
          return (bookAnalytics as any).getDataPointCount();
        }
        return (bookAnalytics as any)?.snapshots ?? this.bookTracker.getDataPointCount?.() ?? 0;
      },
      0,
      { component: 'BookTracker', operation: 'getDataPointCount' }
    );

    const tradeObservations = this.safeExecute(
      () => {
        if (tradePatterns && typeof (tradePatterns as any).getDataPointCount === 'function') {
          return (tradePatterns as any).getDataPointCount();
        }
        return (tradePatterns as any)?.priceImpactObservations?.observations?.length ??
               this.tradeAnalyzer.getDataPointCount?.() ?? 0;
      },
      0,
      { component: 'TradeAnalyzer', operation: 'getDataPointCount' }
    );

    const params = adaptiveParams.getCurrentParameters();

    return {
      bookSnapshots,
      tradeObservations,
      isReady: bookSnapshots >= params.minimumDataPoints && tradeObservations >= 5,
      recommendedMinimumData: {
        bookSnapshots: Math.max(50, params.minimumDataPoints * 2),
        tradeObservations: 20
      },
      errors: this.getRecentErrors(5),
      cacheStats: analyticsCacheManager.getAllStats(),
      adaptiveParams: params
    };
  }

  // Reset all data (useful for new trading sessions)
  reset() {
    this.safeExecute(
      () => {
        this.bookTracker = new BookStateTracker();
        this.tradeAnalyzer = new TradePatternAnalyzer();
        this.errors = [];
        analyticsCacheManager.clearAll();
        adaptiveParams.reset();
        // console.log('🔄 Analytics integrator reset successfully');
      },
      undefined,
      { component: 'Integrator', operation: 'reset' }
    );
  }

  // Health check method
  getHealthStatus(): {
    status: 'healthy' | 'degraded' | 'unhealthy';
    issues: string[];
    uptime: number;
    errorRate: number;
  } {
    const issues: string[] = [];
    const recentErrors = this.errors.filter(e => Date.now() - e.timestamp < 300000); // 5 minutes
    const errorRate = recentErrors.length / 300; // errors per second

    let status: 'healthy' | 'degraded' | 'unhealthy' = 'healthy';

    if (errorRate > 0.1) {
      status = 'unhealthy';
      issues.push(`High error rate: ${errorRate.toFixed(3)} errors/sec`);
    } else if (errorRate > 0.05) {
      status = 'degraded';
      issues.push(`Elevated error rate: ${errorRate.toFixed(3)} errors/sec`);
    }

    // Check cache performance
    const cacheStats = analyticsCacheManager.getAllStats();
    const avgHitRate = Object.values(cacheStats).reduce((sum, stat) => sum + stat.hitRate, 0) / Object.keys(cacheStats).length;

    if (avgHitRate < 0.3) {
      if (status === 'healthy') status = 'degraded';
      issues.push(`Low cache hit rate: ${(avgHitRate * 100).toFixed(1)}%`);
    }

    // Check for severe errors
    const severeErrors = recentErrors.filter(e => e.severity === 'high');
    if (severeErrors.length > 0) {
      status = 'unhealthy';
      issues.push(`${severeErrors.length} severe errors in last 5 minutes`);
    }

    return {
      status,
      issues,
      uptime: Date.now(), // Simplified - could track actual start time
      errorRate
    };
  }
}

// Utility function to create a configured integrator instance
export function createObservableIntegrator(): ObservableAnalyticsIntegrator {
  return new ObservableAnalyticsIntegrator();
}